import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FileCheck, 
  Upload, 
  Download, 
  Eye, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  FileText,
  Calendar,
  User
} from 'lucide-react';

const AuditorOpinion = ({ onSubmit, onPrevious, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    opinionFile: initialData.opinionFile || null,
    opinionStatus: initialData.opinionStatus || 'pending',
    uploadDate: initialData.uploadDate || null,
    uploadedBy: initialData.uploadedBy || null,
    comments: initialData.comments || ''
  });

  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = (file) => {
    if (file && file.type === 'application/pdf') {
      setFormData({
        ...formData,
        opinionFile: file,
        opinionStatus: 'uploaded',
        uploadDate: new Date().toISOString(),
        uploadedBy: 'Current User' // This would come from auth context
      });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFormData({
      ...formData,
      opinionFile: null,
      opinionStatus: 'pending',
      uploadDate: null,
      uploadedBy: null
    });
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploaded':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploaded':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'uploaded':
        return t('workflow.auditQA.auditorOpinion.uploaded');
      case 'pending':
        return t('workflow.auditQA.auditorOpinion.pending');
      default:
        return t('workflow.auditQA.auditorOpinion.notUploaded');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {t('workflow.auditQA.auditorOpinion.title')}
        </h2>
        
        <p className="text-gray-600 mb-8">
          {t('workflow.auditQA.auditorOpinion.description')}
        </p>

        {/* Status Overview */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.auditorOpinion.status')}
          </h3>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getStatusIcon(formData.opinionStatus)}
                <div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(formData.opinionStatus)}`}>
                    {getStatusText(formData.opinionStatus)}
                  </span>
                </div>
              </div>
              
              {formData.uploadDate && (
                <div className="text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(formData.uploadDate).toLocaleDateString()}
                  </div>
                  {formData.uploadedBy && (
                    <div className="flex items-center mt-1">
                      <User className="w-4 h-4 mr-1" />
                      {formData.uploadedBy}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.auditorOpinion.uploadOpinion')}
          </h3>
          
          {!formData.opinionFile ? (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <FileCheck className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {t('workflow.auditQA.auditorOpinion.dragDropTitle')}
              </p>
              <p className="text-gray-500 mb-4">
                {t('workflow.auditQA.auditorOpinion.dragDropDescription')}
              </p>
              
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileInput}
                className="hidden"
                id="opinion-upload"
              />
              <label
                htmlFor="opinion-upload"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('workflow.auditQA.auditorOpinion.selectFile')}
              </label>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div>
                    <h4 className="text-sm font-medium text-green-800">
                      {formData.opinionFile.name}
                    </h4>
                    <p className="text-xs text-green-600">
                      {(formData.opinionFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="p-2 text-green-600 hover:text-green-700 transition-colors"
                    title={t('workflow.auditQA.auditorOpinion.preview')}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="p-2 text-green-600 hover:text-green-700 transition-colors"
                    title={t('workflow.auditQA.auditorOpinion.download')}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="p-2 text-red-600 hover:text-red-700 transition-colors"
                    title={t('workflow.auditQA.auditorOpinion.remove')}
                  >
                    <AlertCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.auditorOpinion.comments')}
          </h3>
          
          <textarea
            value={formData.comments}
            onChange={(e) => setFormData({...formData, comments: e.target.value})}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('workflow.auditQA.auditorOpinion.commentsPlaceholder')}
          />
        </div>

        {/* Summary Actions */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.auditorOpinion.summaryActions')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              type="button"
              className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <FileText className="w-5 h-5 text-gray-600 mr-2" />
                <span className="font-medium text-gray-900">
                  {t('workflow.auditQA.auditorOpinion.exportQueue')}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {t('workflow.auditQA.auditorOpinion.exportQueueDescription')}
              </p>
            </button>
            
            <button
              type="button"
              className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <Download className="w-5 h-5 text-gray-600 mr-2" />
                <span className="font-medium text-gray-900">
                  {t('workflow.auditQA.auditorOpinion.generateBundle')}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {t('workflow.auditQA.auditorOpinion.generateBundleDescription')}
              </p>
            </button>
            
            <button
              type="button"
              className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 text-gray-600 mr-2" />
                <span className="font-medium text-gray-900">
                  {t('workflow.auditQA.auditorOpinion.markResolved')}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {t('workflow.auditQA.auditorOpinion.markResolvedDescription')}
              </p>
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="button"
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            {t('workflow.auditQA.auditorOpinion.runPreChecks')}
          </button>
        </div>
    </div>
  );
};

export default AuditorOpinion;
